import React, { useCallback, useEffect, useState } from "react";
import { Star, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, arr } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import EmptyState from "@/components/common/EmptyState";
import { TableSkeleton } from "@/components/common/Loading";
import ReviewItem from "@/components/client/ReviewItem";
import Pagination from "@/components/common/Pagination";

const PAGE_LIMIT = 20;
const FILTERS = ["all", "replied", "not_replied", "negative", "this_month", "last_7"];

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [rating, setRating] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(async (p = 1, f = filter, r = rating) => {
    setLoading(true);
    try {
      const params = { page: p, limit: PAGE_LIMIT };
      if (f !== "all") params.filter = f;
      if (r) params.rating = r;
      const { data } = await api.get("/reviews", { params });
      setReviews(arr(data?.data));
      setPagination({ total: data.total, page: data.page, totalPages: data.totalPages });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [filter, rating]);

  useEffect(() => { load(1, filter, rating); }, [filter, rating]);

  const handlePage = (p) => { setPage(p); load(p, filter, rating); };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/reviews/sync");
      toast.success(data.message || "Reviews synced");
      load(1, filter, rating);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSyncing(false);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/reviews/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "reviews.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reviews"
        subtitle="Manage and reply to customer reviews"
        icon={Star}
        actions={
          <div className="flex gap-2">
            <button onClick={exportCsv} className="btn-outline flex items-center gap-1 text-sm">
              <Download className="h-4 w-4" /> Export
            </button>
            <button onClick={sync} disabled={syncing} className="btn-primary flex items-center gap-1 text-sm">
              <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Sync Google"}
            </button>
          </div>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => { setFilter(f); setPage(1); }}
            className={`rounded-xl border px-3 py-1.5 text-sm font-medium capitalize transition-colors ${
              filter === f ? "border-brand-600 bg-brand-50 text-brand-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {f.replace(/_/g, " ")}
          </button>
        ))}
        {[5, 4, 3, 2, 1].map((r) => (
          <button
            key={r}
            onClick={() => { setRating(rating === String(r) ? "" : String(r)); setPage(1); }}
            className={`rounded-xl border px-3 py-1.5 text-sm font-medium transition-colors ${
              rating === String(r) ? "border-amber-500 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {"★".repeat(r)}
          </button>
        ))}
      </div>

      {loading ? (
        <TableSkeleton />
      ) : reviews.length === 0 ? (
        <EmptyState icon={Star} title="No reviews found" description="Sync Google reviews or adjust your filters." />
      ) : (
        <div className="space-y-4">
          {reviews.map((r) => <ReviewItem key={r.id || r._id} review={r} onChange={() => load(page, filter, rating)} />)}
          <Pagination page={pagination.page} totalPages={pagination.totalPages} total={pagination.total} limit={PAGE_LIMIT} onPage={handlePage} />
        </div>
      )}
    </div>
  );
}
